import {
  faInfoCircle,
  faMinus,
  faPlus,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons'
import { NumberInput, Box, Button, Flex, Text } from '@mantine/core'
import { clsx } from 'clsx'
import { useEffect } from 'react'

import { useTranslation } from '@flecto/translations'
import {
  type CoreItemAvailability,
  type ParameterID,
  type CoreItem,
  type CoreProductSelection,
  type CoreProduct,
} from '@flecto/types'
import {
  AvatarItem,
  Icon,
  PopoverProductBundleItems,
  TextProductTitle,
} from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { TextProductPrice } from '../../../products'
import { TooltipBookingProductOverbooking } from '../TooltipBookingProductOverbooking/TooltipBookingProductOverbooking'
import { TooltipBookingProductUnavailable } from '../TooltipBookingProductUnavailable/TooltipBookingProductUnavailable'
import styles from './styles.module.css'
import { useItemsAvailabilityCalculator } from './useItemsAvailabilityCalculator'
export type ItemLineProps = {
  productSelected: CoreProductSelection
  bookingId?: ParameterID
  onChangeItemQuantity?: (product: CoreProductSelection) => void
}

export type CoreItemAvailabilityLocal = CoreItemAvailability & {
  original_remaining_quantity: number
}

export function RowProduct({
  productSelected,
  bookingId,
  ...props
}: ItemLineProps) {
  const { t: tShared } = useTranslation('shared', { keyPrefix: 'shared' })
  const { isMobile } = useMediaQuery()

  const {
    quantity,
    isInfinite,
    isOverbooked,
    isUnavailable,
    colorAvailability,
    infiniteSymbol,
    remainingQuantity,
    inputMaxQuantity,
    increaseQuantity,
    decreaseQuantity,
    updateQuantity,
  } = useItemsAvailabilityCalculator({
    quantityStart: productSelected?.bookingProduct?.quantity ?? 0,
    bookingId,
    productSelected,
  })

  const hasPickedUpQuantity =
    productSelected.bookingProduct?.picked_up_quantity !== undefined &&
    productSelected.bookingProduct.picked_up_quantity ===
      productSelected.bookingProduct.quantity
  const disableDecreaseButton =
    productSelected.bookingProduct?.quantity !== undefined &&
    productSelected.bookingProduct?.quantity <=
      (productSelected.bookingProduct?.picked_up_quantity ?? 0)

  useEffect(() => {
    if (!productSelected.bookingProduct && quantity > 0) {
      productSelected.bookingProduct = {
        item_id: productSelected.product?.id,
        quantity,
        tax_rate: productSelected.product?.item_attributes?.tax_rate,
        taxable: productSelected.product?.item_attributes?.taxable,
      }
    } else if (productSelected.bookingProduct && quantity <= 0) {
      productSelected.bookingProduct.quantity = 0
    } else if (productSelected.bookingProduct && quantity > 0) {
      productSelected.bookingProduct.quantity = quantity

      // There is a possibility to update quantity when the item is picked up
      // In this case we have to update the picked up quantity
      if ((productSelected.bookingProduct.picked_up_quantity ?? 0) > quantity) {
        productSelected.bookingProduct.picked_up_quantity = quantity
      }
    }

    props.onChangeItemQuantity?.(productSelected)
  }, [quantity])

  return (
    <Box
      className={clsx(styles.itemLine, {
        [styles.itemSelected]: productSelected.bookingProduct,
      })}
    >
      <AvatarItem item={productSelected.product?.item_attributes as CoreItem} />

      <Box>
        <Flex direction='column'>
          <Flex align='center'>
            <TextProductTitle
              product={productSelected.product as CoreProduct}
            />
          </Flex>
          {isOverbooked && <TooltipBookingProductOverbooking />}
          {isUnavailable && <TooltipBookingProductUnavailable />}
        </Flex>
      </Box>

      <TextProductPrice
        product={productSelected.product as CoreProduct}
        c='dimmed'
        ta='right'
        mr='sm'
        fallbackText='-'
        className={clsx({
          [styles.noWrap]: isMobile,
        })}
      />

      <PopoverProductBundleItems
        product={productSelected.product as CoreProduct}
        qtdSelected={quantity}
      >
        <Flex align='center'>
          <Text miw={80} ta='right' mr='sm' c={colorAvailability}>
            {isInfinite ? infiniteSymbol : remainingQuantity}{' '}
            {tShared('in_stock')}
          </Text>
          {Boolean(productSelected.product?.bundle) && (
            <Icon icon={faInfoCircle} color='gray.7' />
          )}
        </Flex>
      </PopoverProductBundleItems>

      <Flex align='center' gap='sm'>
        <Button
          size='sm'
          variant='outline'
          px={0}
          h={30}
          w={30}
          disabled={
            quantity <= 0 ||
            (!isInfinite && hasPickedUpQuantity && !remainingQuantity) ||
            disableDecreaseButton
          }
          onClick={() => decreaseQuantity()}
        >
          <Text span>
            <Icon icon={faMinus} size='sm' />
          </Text>
        </Button>

        <NumberInput
          id={`input-quantity-${productSelected.product?.id}`}
          w={50}
          size='sm'
          min={0}
          step={1}
          max={inputMaxQuantity}
          placeholder={'0'}
          value={quantity}
          className={styles.inputQuantity}
          onChange={(value) => {
            const numValue = Number(value)
            const adjustedValue =
              inputMaxQuantity && numValue > inputMaxQuantity ? 1 : numValue

            updateQuantity(Number(adjustedValue))
          }}
          disabled={
            remainingQuantity === 0 &&
            !productSelected.product?.item_attributes?.allow_overbooking &&
            !isInfinite
          }
          hideControls
        />

        <Button
          size='sm'
          variant='outline'
          px={0}
          h={30}
          w={30}
          disabled={
            remainingQuantity <= 0 &&
            !productSelected.product?.item_attributes?.allow_overbooking &&
            productSelected.product?.item_attributes?.track_quantity
          }
          onClick={() => increaseQuantity()}
        >
          <Icon icon={faPlus} size='sm' />
        </Button>
      </Flex>

      <Box>
        {productSelected.bookingProduct && !disableDecreaseButton && (
          <Box
            w={20}
            style={{ float: 'right', cursor: 'pointer' }}
            onClick={() => updateQuantity(0)}
          >
            <Icon icon={faTrashCan} color='gray.7' />
          </Box>
        )}
      </Box>
    </Box>
  )
}
